/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import InstanceType from './InstanceType';
import QsClassifierType from './QsClassifierType';
import ReferenceResolver from './ReferenceResolver';
import IdGenerator from './IdGenerator';
import EditModeUtils from './EditModeUtils';
import MoserObjectUtils from './MoserObjectUtils';
import DataModuleExtractor from './DataModuleExtractor';
import UsageType from './UsageType';
import ItemScanner from './ItemScanner';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (static) // QsClassifierType
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // getFirstPart, getQueryItemByIdAndQuerySubject, getSecondPart
//@import com/ibm/bi/platform/moser/core/module/util/IdGenerator (static) // generateNewUseSpecId
//@import com/ibm/bi/platform/moser/common/utils/EditModeUtils (static) // setEditMode
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getItems, getList, getQueryItems
//@import com/ibm/bi/platform/moser/core/module/util/DataModuleExtractor (static) // getUseSpecById
//@import com/ibm/bi/platform/moser/common/generated/metadata/UsageType (static) // UsageType
//@import com/ibm/bi/platform/moser/common/utils/ItemScanner (runtime) // collectIdentifiers

/**
 * Utility class. If a function is used only for traverse, don't use getXXX (it might create an empty list). use basicGetXXX.
 * @author  mohamedelgemaiey
 */
export default class QuerySubjectUtils extends BaseMoserObject{

	/**
	 * Break the query subject reference and convert it to a copy. (unlink)
	 * @param  qs: the query subject
	 * @param  module: The module that includes the query subject
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static convertReferenceQuerySubjectToCopy(qs, module) {
		if (qs.getInstanceType() != InstanceType.REFERENCE) {
			throw new Error("The query subject instance type is not reference.");
		}
		var qsCls = qs.getClassifier();
		if (qsCls == QsClassifierType.QUERY_SUBJECT || !qsCls) {
			var qsRef = qs.getRef()[0];
			var baseModuleUseSpecId = ReferenceResolver.getFirstPart(qsRef);
			var baseModuleuseSpec = QuerySubjectUtils.getUseSpecById(module, baseModuleUseSpecId);
			if (baseModuleuseSpec) {
				var newBaseModuleId;
				var newBaseModuleUseSpec = QuerySubjectUtils._getMUseSpecByStoreId(module, baseModuleuseSpec.getStoreID());
				if (!newBaseModuleUseSpec) {
					newBaseModuleUseSpec = baseModuleuseSpec.clone();
					newBaseModuleId = IdGenerator.generateNewUseSpecId(module);
					newBaseModuleUseSpec.setIdentifier(newBaseModuleId);
					module.addUseSpec(newBaseModuleUseSpec);
				} else {
					newBaseModuleId = newBaseModuleUseSpec.getIdentifier();
				}
				qs.removeOriginalRefAt(0);
				var origRef = newBaseModuleId + "." + ReferenceResolver.getSecondPart(qsRef);
				qs.removeRefAt(0);
				qs.addRefAt(0, origRef);
			}
			if (baseModuleUseSpecId != null && baseModuleUseSpecId.length === 0) {
				qs.setInstanceType(InstanceType.COPY);
			} else {
				qs.setInstanceType(null);
			}
		} else {
			qs.setInstanceType(null);
		}
		QuerySubjectUtils.clearOriginalRef(qs);
		qs.setTreatAs(null);
		EditModeUtils.setEditMode(qs, null);
		QuerySubjectUtils.clearPropertyOverride(qs);
		var qsId = qs.getIdentifier();
		if (qsId != null && !(qsId.length === 0)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = module.getRelationship(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var rel = __exp_enFor0[__i_enFor0];
				if (qsId == rel.getLeft().getRef() || qsId == rel.getRight().getRef()) {
					rel.setInstanceType(null);
					rel.setEditMode(null);
					rel.setRef(null);
					QuerySubjectUtils._clearPropertyOverrideList(rel);
					if (qsId == rel.getLeft().getRef()) {
						rel.getLeft().setReferencedObject(qs);
					}
					if (qsId == rel.getRight().getRef()) {
						rel.getRight().setReferencedObject(qs);
					}
				}
			}
		}
	}

	/**
	 * Clears Ref in QS
	 * @param {QuerySubject} qs
	 */
	static clearRef(qs) {
		if (!qs.basicGetRef()) {
			return;
		}
		for (var i = qs.basicGetRef().length - 1; i >= 0; --i) {
			qs.removeRefAt(i);
		}
	}

	/**
	 * Clears Original Ref in QS
	 * @param {QuerySubject} qs
	 */
	static clearOriginalRef(qs) {
		if (!qs.basicGetOriginalRef()) {
			return;
		}
		for (var i = qs.basicGetOriginalRef().length - 1; i >= 0; --i) {
			qs.removeOriginalRefAt(i);
		}
	}

	/**
	 * Clears embedded filter in QS
	 * @param {QuerySubject} qs
	 */
	static clearEmbeddedFilter(qs) {
		if (qs.basicGetFilter()) {
			for (var i = qs.basicGetFilter().length - 1; i >= 0; --i) {
				qs.removeFilterAt(i);
			}
		}
	}

	/**
	 * Adds a list of refs to query subject
	 * @param {QuerySubject} qs
	 * @param {Array} refs
	 */
	static addAllRef(qs, refs) {
		if (!refs || refs.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = refs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var ref = __exp_enFor0[__i_enFor0];
			qs.addRef(ref);
		}
	}

	/**
	 * Clear the propertyOverride list in the qs and all its items
	 * @param {QuerySubject} qs
	 */
	static clearPropertyOverride(qs) {
		QuerySubjectUtils._clearPropertyOverrideList(qs);
		var allItems = MoserObjectUtils.getItems(qs);
		for (var __i_enFor0 = 0, __exp_enFor0 = allItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._clearPropertyOverrideList(obj);
		}
	}

	/**
	 * Clear the list using removeAt so the notification is fired
	 * @param {ObjectType} obj
	 */
	static _clearPropertyOverrideList(obj) {
		var list = MoserObjectUtils.getList(obj.basicGetPropertyOverride());
		for (var i = list.length - 1; i >= 0; --i) {
			obj.removePropertyOverrideAt(i);
		}
	}

	static _getMUseSpecByStoreId(module, storeId) {
		var uSpecs = MoserObjectUtils.getList(module.basicGetUseSpec());
		for (var __i_enFor0 = 0, __exp_enFor0 = uSpecs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpec = __exp_enFor0[__i_enFor0];
			if (storeId == useSpec.getStoreID() && useSpec.getIdentifier().indexOf("M") == 0) {
				return useSpec;
			}
		}
		return null;
	}

	/**
	 * get a usespec in a module using the useSpecId
	 * @param {Module} module
	 * @param {String} useSpecId
	 * @return {UseSpec}
	 */
	static getUseSpecById(module, useSpecId) {
		return DataModuleExtractor.getUseSpecById(module, useSpecId);
	}

	/**
	 * Retrieve <code>QueryItem</code> by identifier within a <code>QuerySubject</code>
	 * @param {String} id  the identifier of the <code>QueryItem</code>
	 * @param {QuerySubject} qs  the <code>QuerySubject</code> of this <code>QueryItem</code>
	 * @return {BaseQueryItemType}  a reference of the <code>QueryItem</code>
	 */
	static getQueryItemById(id, qs) {
		return ReferenceResolver.getQueryItemByIdAndQuerySubject(id, qs, false);
	}

	/**
	 * Get the parent ObjectType of an object.
	 * @param {String} objType
	 * @param {MoserObject} obj
	 * @return {MoserObject}
	 */
	static getParentOfType(objType, obj) {
		if (!obj) {
			return null;
		}
		if (objType == obj.getObjectType()) {
			return obj;
		}
		return QuerySubjectUtils.getParentOfType(objType, obj.getParent());
	}

	/**
	 * Get the parent QuerySubject of an object.
	 * @param {MoserObject} obj
	 * @return {QuerySubject}
	 */
	static getParentQuerySubject(obj) {
		return QuerySubjectUtils.getParentOfType("QuerySubject", obj);
	}

	/**
	 * Get the parent Module of an object.
	 * @param {MoserObject} obj
	 * @return {Module}
	 */
	static getParentModule(obj) {
		return QuerySubjectUtils.getParentOfType("Module", obj);
	}

	/**
	 * update the QuerySubject for typed-in SQL.
	 * @param {QuerySubject} qs  the existing QuerySubject.
	 * @param {QuerySubject} newQS  the QuerySubject from XQE response for describing SQL text..
	 * @return {boolean}  t/f. t means some items are removed.
	 */
	static updateSQLQueryQuerySubject(qs, newQS) {
		var label = newQS.getLabel();
		if (label != null) {
			qs.setLabel(label);
		}
		var sqlQuery = qs.getSqlQuery();
		var newSQLQuery = newQS.getSqlQuery();
		var existingEmpty = QuerySubjectUtils._isEmptySQLQuery(sqlQuery);
		var newEmpty = QuerySubjectUtils._isEmptySQLQuery(newSQLQuery);
		if (existingEmpty && newEmpty) {
			if (newSQLQuery) {
				qs.setSqlQuery(newSQLQuery.clone());
			} else {
				qs.setSqlQuery(null);
			}
			return false;
		}
		var removedId;
		var bDel = [false, false];
		if (newEmpty) {
			removedId = [];
			var projItems = MoserObjectUtils.getList(sqlQuery.basicGetProjectedItem());
			for (var __i_enFor0 = 0, __exp_enFor0 = projItems, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var pItm = __exp_enFor0[__i_enFor0];
				removedId.push(pItm.getIdentifier());
			}
			if (newSQLQuery) {
				qs.setSqlQuery(newSQLQuery.clone());
			} else {
				qs.setSqlQuery(null);
			}
			QuerySubjectUtils._deleteItemsInQS(qs, removedId, bDel);
			return bDel[0] && (bDel[1] || qs.basicGetFilter() && !(qs.basicGetFilter().length==0));
		}
		var addedByExternalName;
		var clonedSQL;
		if (existingEmpty) {
			clonedSQL = newSQLQuery.clone();
			qs.setSqlQuery(clonedSQL);
			addedByExternalName = [];
			var projItems = MoserObjectUtils.getList(clonedSQL.basicGetProjectedItem());
			for (var __i_enFor1 = 0, __exp_enFor1 = projItems, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var pItm = __exp_enFor1[__i_enFor1];
				addedByExternalName.push(pItm.getExternalName());
			}
			QuerySubjectUtils._addQueryItems(qs, addedByExternalName, newQS);
			return false;
		}
		var existingExtNames = QuerySubjectUtils._getExternalNamesFromProjItems(sqlQuery);
		var newExtNames = QuerySubjectUtils._getExternalNamesFromProjItems(newSQLQuery);
		var intersectExtName = QuerySubjectUtils._getIntersect(existingExtNames, newExtNames);
		var delExtName = QuerySubjectUtils._getExcept(existingExtNames, intersectExtName);
		addedByExternalName = QuerySubjectUtils._getExcept(newExtNames, intersectExtName);
		removedId = QuerySubjectUtils._getIdByExternalName(sqlQuery, delExtName);
		var updatedId = QuerySubjectUtils._getUpdateItemIds(intersectExtName, sqlQuery, newSQLQuery);
		clonedSQL = newSQLQuery.clone();
		qs.setSqlQuery(clonedSQL);
		if (removedId && !(removedId.length==0)) {
			QuerySubjectUtils._deleteItemsInQS(qs, removedId, bDel);
		}
		if (updatedId) {
			QuerySubjectUtils._updateItemsInQS(qs, updatedId);
		}
		if (addedByExternalName && !(addedByExternalName.length==0)) {
			QuerySubjectUtils._addQueryItems(qs, addedByExternalName, newQS);
		}
		return bDel[0] && (bDel[1] || qs.basicGetFilter() && !(qs.basicGetFilter().length==0));
	}

	/**
	 * check if sqlQuery is empty.
	 * @param {SqlQueryType} sqlQuery 	sqlQuery.
	 * @return {boolean}  t/f.
	 */
	static _isEmptySQLQuery(sqlQuery) {
		if (!sqlQuery) {
			return true;
		}
		var projItems = sqlQuery.basicGetProjectedItem();
		return !projItems || projItems.length==0;
	}

	/**
	 * get external names.
	 * @param {SqlQueryType} sqlQuery  sqlQuery.
	 * @return {Array}  external names.
	 */
	static _getExternalNamesFromProjItems(sqlQuery) {
		var r = [];
		var projs = MoserObjectUtils.getList(sqlQuery.basicGetProjectedItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = projs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var projItem = __exp_enFor0[__i_enFor0];
			r.push(projItem.getExternalName());
		}
		return r;
	}

	/**
	 * get id from external names.
	 * @param {SqlQueryType} sqlQuery 	- sqlQuery.
	 * @param {Array} extName 	- external names.
	 * @return {Array}  ids;
	 */
	static _getIdByExternalName(sqlQuery, extName) {
		var r = [];
		if (extName.length==0) {
			return r;
		}
		var projs = MoserObjectUtils.getList(sqlQuery.basicGetProjectedItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = projs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var projItem = __exp_enFor0[__i_enFor0];
			var ex = projItem.getExternalName();
			if (extName.indexOf(ex)> -1) {
				r.push(projItem.getIdentifier());
			}
		}
		return r;
	}

	/**
	 * get ProjectedItemType by external name.
	 * @param {SqlQueryType} sqlQuery  - sqlQuery.
	 * @param {String} s  - ext name.
	 * @return {ProjectedItemType}  ProjectedItemType.
	 */
	static _getProjectedItemTypeByExtName(sqlQuery, s) {
		var projs = MoserObjectUtils.getList(sqlQuery.basicGetProjectedItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = projs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var projItem = __exp_enFor0[__i_enFor0];
			var ex = projItem.getExternalName();
			if (s == ex) {
				return projItem;
			}
		}
		return null;
	}

	/**
	 * intersect.
	 * @param {Array} s1 	- set 1.
	 * @param {Array} s2 	- set 2.
	 * @return {Array}  intersect.
	 */
	static _getIntersect(s1, s2) {
		var r = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = s1, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var v = __exp_enFor0[__i_enFor0];
			if (s2.indexOf(v)> -1) {
				r.push(v);
			}
		}
		return r;
	}

	/**
	 * except.
	 * @param {Array} s1 	- set 1.
	 * @param {Array} s2 	- set 2.
	 * @return {Array}  except.
	 */
	static _getExcept(s1, s2) {
		var r = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = s1, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var v = __exp_enFor0[__i_enFor0];
			if (!(s2.indexOf(v)> -1)) {
				r.push(v);
			}
		}
		return r;
	}

	/**
	 * get ids for update.
	 * @param {Array} extName  - external names.
	 * @param {SqlQueryType} sqlQuery  - existing sqlQuery.
	 * @param {SqlQueryType} newSQLQuery  - new SQLQuery.
	 * @return {Object}  ids for update.
	 */
	static _getUpdateItemIds(extName, sqlQuery, newSQLQuery) {
		if (extName.length==0) {
			return null;
		}
		var r;
		for (var __i_enFor0 = 0, __exp_enFor0 = extName, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var s = __exp_enFor0[__i_enFor0];
			var proj = QuerySubjectUtils._getProjectedItemTypeByExtName(sqlQuery, s);
			var newProj = QuerySubjectUtils._getProjectedItemTypeByExtName(newSQLQuery, s);
			if (!r) {
				r = {};
			}
			r[proj.getIdentifier()] = newProj;
		}
		return r;
	}

	/**
	 * delete items from query-subject.
	 * @param {QuerySubject} qs 			- query subject.
	 * @param {Array} removedId 		- ids to be removed.
	 * @param {boolean[]} bDel 			- returned flags.
	 */
	static _deleteItemsInQS(qs, removedId, bDel) {
		var items = qs.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		var delList = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._deleteItemType(itm, delList, removedId, bDel);
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = delList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var delItm = __exp_enFor1[__i_enFor1];
			bDel[0] = true;
			qs.removeItem(delItm);
		}
	}

	/**
	 * delete item.
	 * @param {ItemType} itm  		- item.
	 * @param {Array} delList 	- returned list.
	 * @param {Array} removedId 	- id to be deleted.
	 * @param {boolean[]} bDel 		- returned flags.
	 */
	static _deleteItemType(itm, delList, removedId, bDel) {
		var qi = itm.getQueryItem();
		if (qi) {
			var id = qi.getIdentifier();
			if (id == qi.getExpression()) {
				if (removedId.indexOf(id)> -1) {
					delList.push(itm);
				}
			} else {
				bDel[1] = true;
			}
		} else {
			var fld = itm.getFolder();
			if (fld) {
				QuerySubjectUtils._deleteItemsInFolder(fld, removedId, bDel);
			}
		}
	}

	/**
	 * delete items from folder.
	 * @param {Folder} theFolder 		- folder.
	 * @param {Array} removedId 		- id to be deleted.
	 * @param {boolean[]} bDel 			- returned flags.
	 */
	static _deleteItemsInFolder(theFolder, removedId, bDel) {
		var items = theFolder.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		var delList = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._deleteItemType(itm, delList, removedId, bDel);
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = delList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var delItm = __exp_enFor1[__i_enFor1];
			bDel[0] = true;
			theFolder.removeItem(delItm);
		}
	}

	/**
	 * update item in QS.
	 * @param {QuerySubject} qs 	qs.
	 * @param {Object} updatedId  id to update.
	 */
	static _updateItemsInQS(qs, updatedId) {
		var items = qs.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._updateItemType(itm, updatedId);
		}
	}

	/**
	 * update item.
	 * @param {ItemType} itm  		- item.
	 * @param {Object} updatedId  id to update.
	 */
	static _updateItemType(itm, updatedId) {
		var qi = itm.getQueryItem();
		if (qi) {
			var id = qi.getIdentifier();
			if (id == qi.getExpression()) {
				var proj = updatedId[id];
				if (proj) {
					qi.setNullable(proj.isNullable());
					qi.setDatatype(proj.getDatatype());
					qi.setHighlevelDatatype(proj.getHighlevelDatatype());
				}
			}
		} else {
			var fld = itm.getFolder();
			if (fld) {
				QuerySubjectUtils._updateItemsInFolder(fld, updatedId);
			}
		}
	}

	/**
	 * update items in folder.
	 * @param {Folder} theFolder 		- folder.
	 * @param {Object} updatedId  id to update.
	 */
	static _updateItemsInFolder(theFolder, updatedId) {
		var items = theFolder.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._updateItemType(itm, updatedId);
		}
	}

	/**
	 * collect existing items.
	 * @param {QuerySubject} qs  qs.
	 * @param {Object} existIds  returned ids.
	 */
	static _collectObjectIdsInQS(qs, existIds) {
		var items = qs.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._collectObjectIdsInItem(itm, existIds);
		}
	}

	/**
	 * collect existing items.
	 * @param {ItemType} itm  item.
	 * @param {Object} existIds  returned ids.
	 */
	static _collectObjectIdsInItem(itm, existIds) {
		var qi = itm.getQueryItem();
		if (qi) {
			var id = qi.getIdentifier();
			existIds[id] = 1;
		} else {
			var fld = itm.getFolder();
			if (fld) {
				QuerySubjectUtils._collectObjectIdsInFolder(fld, existIds);
			}
		}
	}

	/**
	 * collect existing items.
	 * @param {Folder} theFolder  theFolder.
	 * @param {Object} existIds  returned ids.
	 */
	static _collectObjectIdsInFolder(theFolder, existIds) {
		var items = theFolder.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			QuerySubjectUtils._collectObjectIdsInItem(itm, existIds);
		}
	}

	/**
	 * add new query items.
	 * @param {QuerySubject} qs  existing QS.
	 * @param {Array} addedByExternalName  new to be added.
	 * @param {QuerySubject} newQS  new QS.
	 */
	static _addQueryItems(qs, addedByExternalName, newQS) {
		var existIds = {};
		QuerySubjectUtils._collectObjectIdsInQS(qs, existIds);
		var sqlQuery = qs.getSqlQuery();
		for (var __i_enFor0 = 0, __exp_enFor0 = addedByExternalName, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var s = __exp_enFor0[__i_enFor0];
			var proj = QuerySubjectUtils._getProjectedItemTypeByExtName(sqlQuery, s);
			var id = proj.getIdentifier();
			var newId = QuerySubjectUtils._makeUnique(existIds, id);
			var aItem = QuerySubjectUtils.getItemTypeByIdAndQuerySubject(id, newQS);
			var cloned = aItem.clone();
			if (!(newId == id)) {
				existIds[newId] = 1;
				var qi = cloned.getQueryItem();
				qi.setIdentifier(newId);
				qi.setIdForExpression(qs.getIdentifier() + "." + newId);
				qi.setExpression(newId);
				proj.setIdentifier(newId);
				proj.setIdForExpression(sqlQuery.getIdentifier() + "." + newId);
			}
			qs.addItem(cloned);
		}
	}

	/**
	 * make it unique.
	 * @param {Object} existIds  existing.
	 * @param {String} validId  id.
	 * @return {String}  new id.
	 */
	static _makeUnique(existIds, validId) {
		var identifier = validId;
		var iSuffix = 0;
		while (existIds.hasOwnProperty(identifier)) {
			identifier = validId + '_' + ++iSuffix;
		}
		return iSuffix > 0 ? validId + '_' + iSuffix : validId;
	}

	/**
	 * get the item type. there is no folder.
	 * @param {String} id  id.
	 * @param {QuerySubject} qs  qs.
	 * @return {ItemType}  ItemType.
	 */
	static getItemTypeByIdAndQuerySubject(id, qs) {
		if (!qs) {
			return null;
		}
		var items = qs.basicGetItem();
		if (!items || items.length==0) {
			return null;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var qi = itm.getQueryItem();
			if (qi) {
				var qiId = qi.getIdentifier();
				if (id == qiId) {
					return itm;
				}
			}
		}
		return null;
	}

	/**
	 * Returns list of references for a query subject. Original Ref for qs references. Query Ref for join or operational query subjects. Ref otherwise.
	 * @param {QuerySubject} qs
	 * @return {Array}  list of references
	 */
	static getQuerySubjectRefOrOriginalRef(qs) {
		var refs;
		if (qs.getInstanceType() == InstanceType.REFERENCE) {
			refs = qs.basicGetOriginalRef();
		} else if (QsClassifierType.JOIN_QUERY_SUBJECT == qs.getClassifier()) {
			refs = qs.getJoinOperation().getQueryRef();
		} else if (QsClassifierType.OPERATION_QUERY_SUBJECT == qs.getClassifier()) {
			refs = qs.getQueryOperation().getQueryRef();
		} else {
			refs = qs.basicGetRef();
		}
		if (!refs) {
			return [];
		}
		return refs;
	}

	/**
	 * collect filters in folder.
	 * @param {Folder} theFolder  folder.
	 * @param {Array} itemFilters  returned filters.
	 */
	static _collectFilterInFolder(theFolder, itemFilters) {
		var items = theFolder.basicGetItem();
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			var f = itm.getFilter();
			if (f) {
				itemFilters.push(f);
			} else {
				var fld = itm.getFolder();
				if (fld) {
					QuerySubjectUtils._collectFilterInFolder(fld, itemFilters);
				}
			}
		}
	}

	/**
	 * get query items with split / filter.
	 * @param {Module} module  module.
	 * @param {QueryItem} exclQI  query item to exclude.
	 * @return {Array}  query items with split / filter.
	 */
	static listComparableQueryItems(module, exclQI) {
		var rt = [];
		var qsList = MoserObjectUtils.getList(module.basicGetQuerySubject());
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(qs), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var qi = __exp_enFor1[__i_enFor1];
				if (qi == exclQI) {
					continue;
				}
				var sp = qi.basicGetSplitDefinition();
				if (!sp || sp.length==0) {
					continue;
				}
				var items = qi.basicGetItem();
				if (!items || items.length==0) {
					continue;
				}
				var itemFilters = [];
				for (var __i_enFor2 = 0, __exp_enFor2 = items, __len_enFor2 = __exp_enFor2.length;
						__i_enFor2 < __len_enFor2; ++__i_enFor2) {
					var itm = __exp_enFor2[__i_enFor2];
					var f = itm.getFilter();
					if (f) {
						itemFilters.push(f);
					} else {
						var fld = itm.getFolder();
						if (fld) {
							QuerySubjectUtils._collectFilterInFolder(fld, itemFilters);
						}
					}
				}
				if (!(itemFilters.length==0)) {
					rt.push(qi);
				}
			}
		}
		return rt;
	}

	static queryItemAllowPostAggregate(qi) {
		var usage = qi.getUsage();
		var qs = QuerySubjectUtils.getParentQuerySubject(qi);
		var expr = qi.getExpression();
		return QuerySubjectUtils.allowPostAggregate(expr, qs, usage, qi);
	}

	static expressionAllowPostAggregate(expr, qs, usage) {
		return QuerySubjectUtils.allowPostAggregate(expr, qs, usage, null);
	}

	static allowPostAggregate(expr, qs, usage, qi) {
		if (!(UsageType.FACT === usage)) {
			return false;
		}
		if (!qs) {
			return false;
		}
		var qsId = qs.getIdentifier();
		var ids = ItemScanner.collectIdentifiers(expr);
		if (ids.length==0) {
			return true;
		}
		var allQIs = {};
		var qis = MoserObjectUtils.getQueryItems(qs);
		for (var __i_enFor0 = 0, __exp_enFor0 = qis, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var aQI = __exp_enFor0[__i_enFor0];
			if (aQI === qi) {
				continue;
			}
			allQIs[aQI.getIdForExpression()] = 1;
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = ids, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var id = __exp_enFor1[__i_enFor1];
			var fullId = QuerySubjectUtils.getNormalized(id, qsId);
			if (!(allQIs.hasOwnProperty(fullId))) {
				return false;
			}
		}
		return true;
	}

	/**
	 * return idforexpression.
	 * @param {String[]} id
	 * @param {String} qsId
	 * @return {String}
	 */
	static getNormalized(id, qsId) {
		var bDot = false;
		var p = QuerySubjectUtils._skipBracket(id[0]);
		var sb = "";
		if (!(qsId == p)) {
			sb+=(qsId);
			bDot = true;
		}
		for (var __i_enFor0 = 0, __len_enFor0 = id.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var s = id[__i_enFor0];
			if (bDot) {
				sb+=(".");
			} else {
				bDot = true;
			}
			sb+=(QuerySubjectUtils._skipBracket(s));
		}
		return sb;
	}

	static _skipBracket(value) {
		var s = value;
		if (s.startsWith("[") && s.endsWith("]")) {
			s = s.substring(1, s.length - 1);
			return s.replace(new RegExp("]]","g"), "]");
		}
		return s;
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/QuerySubjectUtils.js